/**
 * Compose a page's clean HTML with its separately-stored background image.
 *
 * Mirrors the backend `_compose_html_with_bg` helper in carousel.py.
 * Used by the frontend iframe render (Carousel.js preview + enlarged view)
 * so the "decoupled image" architecture stays intact:
 *   - DB stores `pages[i].html` (no data URLs) and `pages[i].bg_image`
 *     (base64 data URL) as separate fields.
 *   - AI edits only touch `html`; the image never enters AI context so it
 *     can never be dropped, duplicated, or shuffled into the wrong selector.
 *   - At render time we compose them back together here.
 *
 * If `bgImage` is falsy → returns `html` unchanged (works for text-only pages
 * AND legacy pages that still carry an inline data URL in their HTML).
 */
export function composeHtmlWithBg(html, bgImage) {
  if (!bgImage || !html) return html || '';

  // Remove any prior decoupled-bg injection so swapping is idempotent
  const cleaned = html.replace(
    /<style[^>]*data-reelscript-decoupled-bg[^>]*>[\s\S]*?<\/style>/gi,
    '',
  );

  // Universal selector list matches the backend implementation.
  const bgBlock = `
    body, body > *, .page, .slide, .container, main, section,
    [class*='page'], [class*='slide'], [class*='container'] {
      background-image: url('${bgImage}') !important;
      background-size: cover !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
    }
  `;
  const styleTag = `<style data-reelscript-decoupled-bg="1">${bgBlock}</style>`;

  const headCloseMatch = /<\/head>/i.exec(cleaned);
  if (headCloseMatch) {
    const idx = headCloseMatch.index;
    return cleaned.slice(0, idx) + styleTag + cleaned.slice(idx);
  }
  return styleTag + cleaned;
}

export default composeHtmlWithBg;
